import { cn } from '@lib/utils';
import { ArrowDownIcon, ArrowUpIcon, CaretSortIcon, EyeNoneIcon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@registry/new-york/ui/dropdown-menu';
import { Column } from '@tanstack/react-table';

import styles from './DataTableColumnHeader.module.scss';
import { TruncatedHeaderTitle } from './TruncatedHeaderTitle/TruncatedHeaderTitle';

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  /** Ellipsizes long titles; hovering reveals the full title over neighboring columns. */
  truncate?: boolean;
}

export const DataTableColumnHeader = <TData, TValue>({
  column,
  title,
  className,
  truncate = false,
}: DataTableColumnHeaderProps<TData, TValue>) => {
  const titleNode = (
    <TruncatedHeaderTitle
      title={title}
      truncate={truncate}
    />
  );

  if (!column.getCanSort()) {
    return (
      <div className={cn(styles.header, truncate && styles.headerTruncate, className)}>
        <Button
          variant="ghost"
          size="sm"
          className={cn(styles.button, styles.buttonStatic, truncate && styles.buttonTruncate)}
        >
          {titleNode}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(styles.header, truncate && styles.headerTruncate, className)}>
      <DropdownMenu>
        <DropdownMenuTrigger className={cn(truncate && styles.triggerTruncate)}>
          <Button
            variant="ghost"
            size="sm"
            className={cn(styles.button, truncate && styles.buttonTruncate)}
          >
            {titleNode}
            {column.getIsSorted() === 'desc' && <ArrowDownIcon className={cn(styles.sortIcon, styles.sortIconSmall)} />}
            {column.getIsSorted() === 'asc' && <ArrowUpIcon className={cn(styles.sortIcon, styles.sortIconSmall)} />}
            {column.getIsSorted() === false && column.getCanSort() && (
              <CaretSortIcon className={cn(styles.sortIcon, styles.sortIconLarge)} />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {column.getCanSort() && (
            <>
              <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
                <ArrowUpIcon className={styles.dropdownIcon} />
                Asc
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
                <ArrowDownIcon className={styles.dropdownIcon} />
                Desc
              </DropdownMenuItem>
            </>
          )}
          {column.getIsSorted() !== false && (
            <DropdownMenuItem onClick={() => column.clearSorting()}>
              <CaretSortIcon className={styles.removeSortIcon} />
              Remove sort
            </DropdownMenuItem>
          )}
          {column.getCanHide() && (
            <>
              {column.getCanSort() && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                <EyeNoneIcon className={styles.dropdownIcon} />
                Hide
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
